import { createFileRoute } from '@tanstack/react-router';

function TermsPage() {
  return (
    <article className="mx-auto max-w-3xl space-y-8">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          Legal
        </p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight">Terms of Service</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          By using Brand Generator you agree to these terms. If you do not agree, please do
          not use the site.
        </p>
      </header>

      <Section title="1. The service">
        <p>
          Brand Generator is a free, browser-based tool that helps you assemble prompts and
          render static ad creatives using image-generation models accessed through your own
          Replicate API token. The site is provided as-is and may be changed, suspended or
          discontinued at any time without notice.
        </p>
      </Section>

      <Section title="2. No warranty">
        <p>
          The service is provided “as is” and “as available”, without warranties of any
          kind, whether express or implied, including but not limited to fitness for a
          particular purpose, accuracy, or non-infringement. We do not warrant that the
          service will be uninterrupted, error-free, or that generated outputs will meet your
          expectations or be suitable for any particular use.
        </p>
      </Section>

      <Section title="3. Your responsibilities">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            You are responsible for the security and cost of your Replicate API token.
            Anyone with access to your browser profile can use it; treat it accordingly.
          </li>
          <li>
            <strong className="text-foreground">
              Replicate spend is solely your responsibility.
            </strong>{' '}
            Generation jobs are billed by Replicate against your own account using the API
            token you supply. You alone are responsible for monitoring and limiting that
            spend. Replicate offers per-account spend limits in your Replicate billing
            settings, and we strongly recommend configuring them before running jobs. The
            operator does not see, control, or insure against your Replicate charges and
            accepts no liability for unexpected, accidental, runaway or third-party-induced
            spend (including through programmatic loops, leaked tokens, or shared browser
            profiles).
          </li>
          <li>
            You are responsible for the prompts you submit and for ensuring you have the
            rights to any inputs you provide (brand assets, reference images, copy).
          </li>
          <li>
            You are responsible for the use of any generated outputs, including obtaining
            the licences required by the model provider before publishing or commercialising
            them. AI-generated outputs may unintentionally reproduce material covered by
            third-party rights, and the copyright protection of AI outputs varies by
            jurisdiction; you are responsible for clearing rights and conducting any
            necessary checks before publishing or commercialising any output.
          </li>
          <li>
            You agree not to use the service to generate unlawful content, infringe third-
            party rights, or attempt to disrupt or compromise the site.
          </li>
        </ul>
      </Section>

      <Section title="4. Third-party services">
        <p>
          Image and text generation are performed by Replicate (replicate.com) under its own
          terms and privacy policy. We are not a party to that contract and have no control
          over what Replicate does with the prompts and tokens you send it. Replicate in
          turn routes requests to the underlying model provider you select in the model
          picker (currently Anthropic, OpenAI, Google, Black Forest Labs, Ideogram, Recraft
          and ByteDance, depending on the model), each of which is a further independent
          provider that may impose additional terms and operate its own data-retention and,
          where applicable, training policies.
        </p>
      </Section>

      <Section title="5. Limitation of liability">
        <p>
          To the maximum extent permitted by German law, the operator shall be liable only
          for damages caused by intent or gross negligence, or arising from injury to life,
          body or health, or under the German Product Liability Act
          (Produkthaftungsgesetz). Liability for slight negligence is limited to the breach
          of essential contractual obligations (Kardinalpflichten) and to damages typically
          foreseeable at the time the service was used. All other liability is excluded.
        </p>
        <p>
          Without prejudice to the foregoing, the operator is in no event liable for
          charges, fees or damages arising from your use of your own Replicate API token,
          since billing for generation jobs occurs entirely between you and Replicate.
        </p>
      </Section>

      <Section title="6. Governing law and venue">
        <p>
          These terms are governed by the laws of the Federal Republic of Germany, excluding
          the UN Convention on Contracts for the International Sale of Goods. Mandatory
          consumer-protection provisions of the country in which you have your habitual
          residence remain unaffected. To the extent legally permissible, the place of
          jurisdiction is Munich.
        </p>
      </Section>

      <Section title="7. Severability">
        <p>
          If any provision of these terms is found to be invalid or unenforceable, the
          remaining provisions remain in full force and effect.
        </p>
      </Section>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="space-y-2 text-[15px] leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

export const Route = createFileRoute('/terms')({
  component: TermsPage,
});
